import DeleteIcon from "@mui/icons-material/Delete";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import DoneIcon from "@mui/icons-material/Done";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import swal from "sweetalert";
import axiosInstance from "../utils/axiosInstance";
import { checkPermission } from "../utils/checkPermission"; // assume this function checks for admin role
import { formatCustomDate } from "../utils/formatters";
import ConfirmationModal from "./ConfirmationModal";
import { useSidebarCounts } from "../context/SidebarCountsContext";

export default function ForumPost({ post, onDelete, onUpdate }) {
  const { updateCount } = useSidebarCounts();

  const [openConfirm, setOpenConfirm] = useState(false);
  const [type, setType] = useState();
  const [id, setId] = useState();

  const handleModalClose = () => {
    setOpenConfirm(false);
    setType(null);
    setId(null);
  };

  const handleDelete = async (type, id) => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/${type}/${id}`,
      );
      if (response.status === 200) {
        handleModalClose();
        onDelete();
        updateCount();
        swal("Deleted successfully!", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to delete. Please try again.", { icon: "error" });
      handleModalClose();
    }
  };

  const approveOrRejectPost = async (id, is_approved) => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/forums/${id}/approve-or-reject`,
        {
          is_approved,
        },
      );
      if (response.status === 200) {
        onUpdate();
        updateCount();
        swal("Approved status successfully!", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to update. Please try again.", { icon: "error" });
    }
  };

  const approveOrRejectComment = async (id, is_approved) => {
    try {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/comments/${id}/approve-or-reject`,
        {
          is_approved,
        },
      );
      if (response.status === 200) {
        onUpdate();
        updateCount();
        swal("Approved status successfully!", { icon: "success" });
      }
    } catch (error) {
      console.error(error);
      swal("Failed to update. Please try again.", { icon: "error" });
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={3} sx={{ mb: 2 }}>
        <Card>
          <Box display="flex" flexDirection={{ xs: "column", md: "row" }}>
            <Box
              display="flex"
              flexDirection="column"
              p={2}
              alignItems="center"
            >
              <Avatar
                src={`${process.env.REACT_APP_STORAGE_URL}/${post.user.profile_picture}`}
                alt={post.user.full_name.charAt(0)}
                sx={{ width: 80, height: 80, mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary">
                {post.user.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Joined: {formatCustomDate(post.user.date_registered)}
              </Typography>
            </Box>

            <Box flex={1} display="flex" flexDirection="column">
              <CardMedia
                component="img"
                height="200"
                image={`${process.env.REACT_APP_STORAGE_URL}/${post.cover_img}`}
                alt={post.title}
                sx={{
                  objectFit: "contain",
                  backgroundColor: "grey.300",
                  borderRadius: "10px",
                }}
              />
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {post.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {post.description}
                    </Typography>
                    <Typography variant="caption" display="block" gutterBottom>
                      Posted on: {formatCustomDate(post.post_date)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="h6" textAlign="right">
                      {post.likes} Likes
                    </Typography>
                  </Box>

                  <Box>
                    {checkPermission("forum-update") && (
                      <>
                        {post.is_approved !== true && (
                          <Tooltip title="Approve">
                            <IconButton
                              color="error"
                              onClick={() => approveOrRejectPost(post.id, true)}
                            >
                              <DoneIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(post.is_approved === true ||
                          post.is_approved === null) && (
                          <Tooltip title="Reject">
                            <IconButton
                              color="error"
                              onClick={() =>
                                approveOrRejectPost(post.id, false)
                              }
                            >
                              <DoDisturbIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {checkPermission("forum-delete") && (
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() => {
                            setOpenConfirm(true);
                            setType("forums");
                            setId(post.id);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {post.comments.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="h6" gutterBottom>
                      Comments
                    </Typography>
                    <List>
                      {post.comments.map((comment) => (
                        <ListItem key={comment.id} alignItems="flex-start">
                          <Avatar
                            src={`${process.env.REACT_APP_STORAGE_URL}/${comment.user.profile_picture}`}
                            alt={comment.user.full_name.charAt(0)}
                            sx={{ mr: 2 }}
                          />
                          <ListItemText
                            primary={comment.comment}
                            secondary={`Comment by: ${comment.user.full_name} on ${formatCustomDate(comment.post_date)}`}
                          />
                          {comment.is_approved !== true && (
                            <Tooltip title="Approve">
                              <IconButton
                                color="error"
                                onClick={() =>
                                  approveOrRejectComment(comment.id, true)
                                }
                              >
                                <DoneIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(comment.is_approved === true ||
                            comment.is_approved === null) && (
                            <Tooltip title="Reject">
                              <IconButton
                                color="error"
                                onClick={() =>
                                  approveOrRejectComment(comment.id, false)
                                }
                              >
                                <DoDisturbIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Show delete button for admin on comment */}
                          {checkPermission("comment-delete") && (
                            <IconButton
                              color="error"
                              onClick={() => {
                                setOpenConfirm(true);
                                setType("comments");
                                setId(comment.id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                          {comment.replies.length > 0 && (
                            <List>
                              {comment.replies.map((reply) => (
                                <ListItem
                                  key={reply.id}
                                  alignItems="flex-start"
                                  sx={{ pl: 4 }}
                                >
                                  <Avatar
                                    src={`${process.env.REACT_APP_STORAGE_URL}/${reply.user.profile_picture}`}
                                    alt={reply.user.full_name.charAt(0)}
                                    sx={{ mr: 2 }}
                                  />
                                  <ListItemText
                                    primary={reply.comment}
                                    secondary={`Reply by: ${reply.user.full_name} on ${formatCustomDate(reply.post_date)}`}
                                  />
                                  {/* Show delete button for admin on reply */}
                                  {checkPermission("comment-delete") && (
                                    <IconButton
                                      color="error"
                                      onClick={() => {
                                        setOpenConfirm(true);
                                        setType("comments");
                                        setId(reply.id);
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </CardContent>
            </Box>
          </Box>
        </Card>
      </Box>

      {openConfirm && type && id && (
        <ConfirmationModal
          open={openConfirm}
          onClose={handleModalClose}
          onConfirm={() => handleDelete(type, id)}
          title={`Are you sure you want to delete this ${type}?`}
          message={
            <span>
              The <strong>{type}</strong> will be delete. Note that this action
              is not reversible.
            </span>
          }
        />
      )}
    </>
  );
}
